import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardStats, getExpiringDocuments, getRecentInvoices, getOverdueInvoices, getWalletDashboardStats, getClients } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatCurrency, formatDate, getExpiryStatusClass, getInvoiceStatusClass } from '@/lib/utils';
import {
  ClipboardList,
  DollarSign,
  FileWarning,
  AlertTriangle,
  Users,
  Building2,
  TrendingUp,
  Receipt,
  Clock,
  ArrowUpRight,
  ArrowRight,
  Sparkles,
  Wallet
} from 'lucide-react';
import QuickActionsHub from '@/components/QuickActionsHub';

const StatCard = ({ title, value, subtext, icon: Icon, iconBg, iconColor, accentColor, delay = 0 }) => (
  <div 
    className="stat-card group" 
    style={{ animationDelay: `${delay}ms` }}
    data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="absolute top-0 left-0 right-0 h-1 rounded-t-xl" style={{ background: accentColor || 'linear-gradient(90deg, hsl(188 64% 38%), hsl(164 62% 40%))' }} />
    <div className="flex items-start justify-between">
      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{title}</p>
        <p className="text-3xl font-bold text-slate-800 tracking-tight">{value}</p>
        {subtext && <p className="text-xs text-slate-500">{subtext}</p>}
      </div>
      <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center transition-transform duration-200 group-hover:scale-110 group-hover:rotate-3`}>
        <Icon className={`w-6 h-6 ${iconColor}`} />
      </div>
    </div>
  </div>
);

const QuickStatCard = ({ icon: Icon, value, label, color }) => (
  <div className="modern-card p-4 flex items-center gap-4 group cursor-default">
    <div className={`w-11 h-11 rounded-xl ${color} flex items-center justify-center transition-transform duration-200 group-hover:scale-105`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-xs font-medium text-slate-500">{label}</p>
    </div>
  </div>
);

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [walletStats, setWalletStats] = useState(null);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, expiringRes, recentRes, overdueRes, walletRes, clientsRes] = await Promise.all([
        getDashboardStats(),
        getExpiringDocuments(30, 10),
        getRecentInvoices(5),
        getOverdueInvoices(5),
        getWalletDashboardStats().catch(() => ({ data: null })),
        getClients({ limit: 200 })
      ]);
      setStats(statsRes.data);
      setExpiringDocs(expiringRes.data);
      setRecentInvoices(recentRes.data);
      setOverdueInvoices(overdueRes.data);
      setWalletStats(walletRes.data);
      setClients(clientsRes.data.items || []);
    } catch (error) {
      console.error('Failed to load dashboard data', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="skeleton h-8 w-48 rounded-lg" />
            <div className="skeleton h-4 w-64 rounded-lg" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="stat-card">
              <div className="skeleton h-4 w-24 rounded mb-3" />
              <div className="skeleton h-9 w-32 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Quick Actions Hub - Most used shortcuts */}
      <QuickActionsHub clients={clients} onRefresh={loadData} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
            <Sparkles className="w-5 h-5 text-amber-500" />
          </div>
          <p className="text-sm text-slate-500">
            Welcome back! Here's what's happening today.
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-slate-600">
            {new Date().toLocaleDateString('en-AE', { weekday: 'long' })}
          </p>
          <p className="text-xs text-slate-400">
            {new Date().toLocaleDateString('en-AE', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Primary KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatCard
          title="Today's Orders"
          value={stats?.todays_orders || 0}
          subtext={<><span className="text-amber-600 font-medium">{stats?.pending_orders || 0}</span> pending</>}
          icon={ClipboardList}
          iconBg="bg-gradient-to-br from-teal-500 to-emerald-500"
          iconColor="text-white"
          accentColor="linear-gradient(90deg, #14b8a6, #10b981)"
          delay={0}
        />
        <StatCard
          title="Today's Revenue"
          value={formatCurrency(stats?.todays_revenue)}
          subtext={<>Month: <span className="font-medium">{formatCurrency(stats?.month_collected)}</span></>}
          icon={DollarSign}
          iconBg="bg-gradient-to-br from-emerald-500 to-green-500"
          iconColor="text-white"
          accentColor="linear-gradient(90deg, #10b981, #22c55e)"
          delay={50}
        />
        <StatCard
          title="Expiring Documents"
          value={stats?.expiring_this_week || 0}
          subtext={<><span className="text-red-600 font-medium">{stats?.expired_count || 0}</span> already expired</>}
          icon={FileWarning}
          iconBg="bg-gradient-to-br from-amber-500 to-orange-500"
          iconColor="text-white"
          accentColor="linear-gradient(90deg, #f59e0b, #f97316)"
          delay={100}
        />
        <StatCard
          title="Outstanding Balance"
          value={formatCurrency(stats?.outstanding_balance)}
          subtext={<>Invoiced: <span className="font-medium">{formatCurrency(stats?.month_invoiced)}</span></>}
          icon={AlertTriangle}
          iconBg="bg-gradient-to-br from-red-500 to-rose-500"
          iconColor="text-white"
          accentColor="linear-gradient(90deg, #ef4444, #f43f5e)"
          delay={150}
        />
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <QuickStatCard icon={Building2} value={stats?.total_clients || 0} label="Total Clients" color="bg-gradient-to-br from-blue-500 to-indigo-500" />
        <QuickStatCard icon={Users} value={stats?.total_workers || 0} label="Total Workers" color="bg-gradient-to-br from-violet-500 to-purple-500" />
        <QuickStatCard icon={TrendingUp} value={formatCurrency(stats?.month_collected)} label="Month Collected" color="bg-gradient-to-br from-emerald-500 to-teal-500" />
        <QuickStatCard icon={Receipt} value={formatCurrency(stats?.month_invoiced)} label="Month Invoiced" color="bg-gradient-to-br from-cyan-500 to-blue-500" />
        <QuickStatCard 
          icon={Wallet} 
          value={formatCurrency(walletStats?.total_wallet_balance || 0)} 
          label={walletStats?.low_balance_clients > 0 ? `${walletStats.low_balance_clients} Low Balance` : 'Total Wallet'} 
          color="bg-gradient-to-br from-teal-500 to-cyan-500" 
        />
      </div>

      {/* Lists Row */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Expiring Documents */}
        <div className="modern-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              <h3 className="font-semibold text-slate-800">Expiring Documents</h3>
              <Badge variant="secondary" className="text-xs">30 Days</Badge>
            </div>
            <Link to="/documents?expiring=30">
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-teal-600 gap-1">
                View All <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
          <div className="p-3">
            {expiringDocs.length === 0 ? (
              <div className="text-center py-8">
                <FileWarning className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No documents expiring soon</p>
              </div>
            ) : (
              <div className="space-y-2">
                {expiringDocs.map((doc, i) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50/80 hover:bg-slate-100 transition-colors cursor-pointer"
                    style={{ animationDelay: `${i * 50}ms` }}
                    data-testid="expiring-doc-row"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{doc.owner_name}</p>
                      <p className="text-xs text-slate-500">{doc.doc_type_name}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={`${getExpiryStatusClass(doc.days_until_expiry)} text-xs font-semibold`}>
                        {doc.days_until_expiry}d
                      </Badge>
                      <span className="text-xs text-slate-400 hidden sm:block">{formatDate(doc.expiry_date)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Overdue Invoices */}
        <div className="modern-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <h3 className="font-semibold text-slate-800">Overdue Invoices</h3>
            </div>
            <Link to="/invoices?status=Overdue">
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-teal-600 gap-1">
                View All <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
          <div className="p-3">
            {overdueInvoices.length === 0 ? (
              <div className="text-center py-8">
                <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No overdue invoices</p>
              </div>
            ) : (
              <div className="space-y-2">
                {overdueInvoices.map((inv, i) => (
                  <Link
                    to={`/invoices/${inv.id}`}
                    key={inv.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-red-50/50 hover:bg-red-50 transition-colors group"
                    style={{ animationDelay: `${i * 50}ms` }}
                    data-testid="overdue-invoice-row"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate group-hover:text-red-700">{inv.client_name}</p>
                      <p className="text-xs text-slate-500">{inv.invoice_number}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-red-600">
                        {formatCurrency(inv.balance_due)}
                      </p>
                      <p className="text-xs text-red-400 flex items-center gap-1 justify-end">
                        <Clock className="w-3 h-3" />
                        {inv.days_overdue}d overdue
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Invoices Table */}
      <div className="modern-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Recent Invoices</h3>
          <Link to="/invoices">
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-teal-600 gap-1">
              View All <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100/80">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Invoice</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Client</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Date</th>
                <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Balance</th>
                <th className="text-center px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recentInvoices.map((inv, i) => (
                <tr key={inv.id} className="hover:bg-teal-50/30 transition-colors" style={{ animationDelay: `${i * 30}ms` }}>
                  <td className="px-5 py-3">
                    <Link to={`/invoices/${inv.id}`} className="text-sm font-medium text-teal-600 hover:text-teal-700 flex items-center gap-1 group">
                      {inv.invoice_number}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-700">{inv.client_name}</td>
                  <td className="px-5 py-3 text-sm text-slate-500">{formatDate(inv.invoice_date)}</td>
                  <td className="px-5 py-3 text-sm text-right font-medium text-slate-800">{formatCurrency(inv.total_amount)}</td>
                  <td className="px-5 py-3 text-sm text-right font-medium text-slate-600">{formatCurrency(inv.balance_due)}</td>
                  <td className="px-5 py-3 text-center">
                    <Badge className={`${getInvoiceStatusClass(inv.status)} text-xs font-medium`}>{inv.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
